import { Injectable } from '@angular/core';
import { NavigationExtras, Router } from '@angular/router';
import { AppRoute } from '../../../routing/routing.config';
import { NGXLogger } from 'ngx-logger';
import { awsconfig, oAuthConfig } from '../../../../environments/environment';
import {
  AppErrorCode, appErrorCodeToErrorStateDict,
  AppErrorState, backendErrorReverseDict, BackEndErrorState,
  defaultAppErrorState,
  defaultBackendErrorState,
  publicErrorCodesDict,
  publicRrrorCodeTemplateVar, referrerURLTemplateVar, supportEmailHrefProto,
  userAgentTemplateVar
} from './error.constants';
import * as Sentry from '@sentry/angular-ivy';
import { Event } from '@sentry/angular';
import { APIHTTPErrorResponse } from '../../models/API.models';
import { AuthService } from '../auth/auth.service';
@Injectable({
  providedIn: 'root',
})
export class ErrorService {
  private _sharedNavigationExtras: NavigationExtras;
  private _sharedNavigationUrl: AppRoute;

  constructor(
    private router: Router,
    private logger: NGXLogger,
    private authService: AuthService,
  ) {}

  get sharedNavigationExtras(): NavigationExtras {
    if (!this._sharedNavigationExtras) {
      // TODO: double check that AppErrorCode.AUTH_ERROR is correct value here
      this.extractAndSetNavExtras(AppErrorCode.UNKNOWN_ERROR);
    }

    return this._sharedNavigationExtras;
  }

  set sharedNavigationExtras(value: NavigationExtras) {
    this._sharedNavigationExtras = value;
  }

  get sharedNavigationUrl(): AppRoute {
    return this._sharedNavigationUrl;
  }

  set sharedNavigationUrl(value: AppRoute) {
    this._sharedNavigationUrl = value;
  }

  fullNavigationUrl(): string {
    return window.location.origin + (this.sharedNavigationUrl || '');
  }

  extractAndSetNavExtras(
    errorCode: AppErrorCode,
    publicErrorCode = ''
  ): NavigationExtras {
    this.sharedNavigationExtras = this.navigationStateForErrorCode(
      errorCode,
      publicErrorCode
    );

    this.logger.debug(
      '[ErrorService] navigation extras: ',
      this.sharedNavigationExtras
    );

    return this.sharedNavigationExtras;
  }

  routeToAuthError(errorCode: AppErrorCode, publicErrorCode = ''): null {
    const code = AppErrorCode[errorCode] || AppErrorCode.UNKNOWN_ERROR;
    this.extractAndSetNavExtras(code, publicErrorCode);
    this.logAppErrorToSentry(code, publicErrorCode);
    this.router.navigate([AppRoute.ERROR_PAGE]);
    return null;
  }

  logAppErrorToSentry(errorCode: AppErrorCode, publicErrorCode = ''): void {
    const event: Event = {
      timestamp: this.sentryTimestampNow(),
      message: errorCode,
      level: 'fatal',
      request: {
        url: this.fullNavigationUrl(),
      },
      extra: {
        errorCode: publicErrorCode || this.publicErrorCodeForAppError(errorCode),
      },
    };
    Sentry.captureEvent(event);
  }

  logAPIErrorToSentry(err: APIHTTPErrorResponse): void {

    const {headers: remove, ...apiRequest } = err;

    const event: Event = {
      timestamp: this.sentryTimestampNow(),
      message: AppErrorCode.API_ERROR,
      level: 'error',
      request: {
        url: this.fullNavigationUrl(),
      },
      extra: {
        apiRequest
      },
    };
    this.logger.debug('Sentry Event: ', event);
    Sentry.captureEvent(event);
  }

  sentryTimestampNow(): number {
    return Math.round((+new Date()) / 1000)
  }

  backEndErrorSuggestedActionUrl(errorState: BackEndErrorState): string | null {
    switch (errorState) {
      case BackEndErrorState.USER_ALREADY_REGISTERED: {
        return this.authService.ssoUrl();
      }
      default:
        return null;
    }
  }

  navigationStateForErrorCode(
    errorCode: AppErrorCode,
    pubErrorCode = ''
  ): NavigationExtras {
    const publicErrorCode = pubErrorCode || this.publicErrorCodeForAppError(errorCode);
    const errorState = this.appErrorStateForErrorCode(errorCode);

    console.log(
      `errorCode: ${errorCode}\nerrorState: ${errorState}\npublicErrorCode: ${publicErrorCode}`
    );

    return { state: { errorState, publicErrorCode } };
  }

  appErrorStateForErrorCode(errorCode: AppErrorCode): AppErrorState {
    return appErrorCodeToErrorStateDict[errorCode] || defaultAppErrorState;
  }

  backEndErrorStateFromAPIErrorCode(apiErrorCode: string): BackEndErrorState {
    const errorCode = backendErrorReverseDict[apiErrorCode];
    return errorCode || defaultBackendErrorState;
  }

  publicErrorCodeForAppError(errorCode: AppErrorCode): string {
    return publicErrorCodesDict[errorCode];
  }

  supportEmailHref(publicErrorCode: string): string {
    const reffererURL = encodeURIComponent(this.fullNavigationUrl());
    this.logger.debug('[ErrorService] - supportEmailHref(): sharedNavigationUrl: ', this.sharedNavigationUrl);
    this.logger.debug('[ErrorService] - supportEmailHref(): reffererURL: ', reffererURL);

    const userAgent = window.navigator.userAgent;
    const errorCode = publicErrorCode || '';

    return supportEmailHrefProto
      .replace(publicRrrorCodeTemplateVar, errorCode)
      .replace(referrerURLTemplateVar, reffererURL)
      .replace(userAgentTemplateVar, userAgent);
  }
}
